// Rejected goods step 4a (2026-10-25; Ahmed's rule, mockup DyrAbPJDgtF8t6mjFfytkv "Door & returns" 3): returning goods we accepted, found
// faulty later. Opened from the receipt page ("Return goods" on a line) or from "New return" on Returns & credits. The return is valued at
// the price paid and recorded against where the goods were charged (that job's cost goes down, Cost Allocation). It does not change the PO
// or the receipt; then Ship and Settle as any return. Server: POST /api/returns-credits/returns (returns-credits.service createLaterReturn).
import React, { useEffect, useState } from 'react';
import { Alert, Input, InputNumber, Modal, Select, message } from 'antd';
import dayjs from 'dayjs';
import { createLaterReturn, CreditCase, LATER_RETURN_REASONS, ProofFile, ReturnableLine } from '../../api';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { money, ProofPicker } from '../receiving/shared';

const lab: React.CSSProperties = { fontWeight: 600, display: 'block', marginBottom: 6 };
const box: React.CSSProperties = { border: '1px solid #d9d9d9', borderRadius: 6, padding: '4px 11px', minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#fafafa' };
const MUTED = '#5f6672';
const req = <span style={{ color: '#cf1322' }}>* </span>;
const q = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

const ReturnGoodsWindow: React.FC<{ target: ReturnableLine | null; onClose: () => void; onSaved: (c: CreditCase) => void }> = ({ target, onClose, onSaved }) => {
  const compact = useCompactLayout();
  const [qty, setQty] = useState<number | null>(null);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<ProofFile[]>([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setQty(null); setReason(undefined); setNote(''); setPhotos([]); }, [target]);

  const t = target;
  const unit = t?.unit ? ` ${t.unit}` : '';
  const vendor = t?.vendor?.name || 'the vendor';
  const value = t && qty ? Math.round(qty * t.price * 100) / 100 : 0;
  const tooMany = !!t && !!qty && qty > t.canReturn + 1e-9;
  const ok = !!t && !!qty && qty > 0 && !tooMany && !!reason && (reason !== 'other' || !!note.trim());

  const save = async () => {
    if (!t || !ok) return;
    setSaving(true);
    try {
      const c = await createLaterReturn({ grnLineId: t.grnLineId, qty: qty!, reason: reason!, note: note.trim() || undefined, photos: photos.length ? photos : undefined });
      message.success(`${c.code || 'Return'} created: to ship`);
      onSaved(c);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not create the return');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!t}
      title={t ? <div style={{ paddingInlineEnd: 28 }}>Return goods · {t.grnId} line {t.lineNo ?? '-'} · <bdi>{t.item.description}</bdi> · <bdi>{vendor}</bdi></div> : null}
      onCancel={onClose}
      onOk={save}
      okText="Create the return"
      okButtonProps={{ disabled: !ok, loading: saving }}
      maskClosable={false}
      width={compact ? 520 : 640}
      destroyOnClose
    >
      {t && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <div>
              <label htmlFor="rg-qty" style={lab}>{req}How many</label>
              <InputNumber
                id="rg-qty" min={0} max={t.canReturn} value={qty} onChange={(v) => setQty(v == null ? null : Number(v))} inputMode="decimal"
                status={tooMany ? 'error' : undefined} style={{ width: '100%' }} addonAfter={t.unit || undefined}
              />
              <div style={{ fontSize: 12, color: tooMany ? '#cf1322' : MUTED, marginTop: 4 }}>
                {tooMany ? `Only ${q(t.canReturn)}${unit} can still go back. ` : ''}of {q(t.accepted)}{unit} accepted{t.returned > 0 ? ` · ${q(t.returned)}${unit} returned already` : ''}
              </div>
            </div>
            <div>
              <label htmlFor="rg-why" style={lab}>{req}Why</label>
              <Select id="rg-why" value={reason} onChange={setReason} placeholder="Choose" options={LATER_RETURN_REASONS.map((r) => ({ value: r.value, label: r.label }))} style={{ width: '100%' }} />
            </div>
            <div>
              <div style={lab}>Value at the price paid</div>
              <div style={box}>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(value)} EGP</span>
                <span style={{ fontSize: 12, color: MUTED }}>{qty ? `${q(qty)} × ${money(t.price)}` : `${money(t.price)} each`}</span>
              </div>
            </div>
            <div>
              <div style={lab}>Photo</div>
              <ProofPicker value={photos} onChange={setPhotos} label="Add a photo" />
            </div>
          </div>
          <div>
            <label htmlFor="rg-note" style={lab}>{reason === 'other' ? req : null}Note</label>
            <Input id="rg-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={reason === 'other' ? 'Why it goes back (required)' : 'Optional, e.g. cracked, found in production'} maxLength={180} />
          </div>
          <Alert
            type="info" showIcon style={{ margin: 0 }}
            message={(
              <span style={{ fontSize: 13 }}>
                Creates a return on Returns &amp; credits (to ship, then waiting for <bdi>{vendor}</bdi>). <b>{t.poId || 'The PO'} and this receipt do not change</b>; only money comes back.
                {t.costCenter && value > 0 ? <> The cost of <code>{t.costCenter.code}</code> goes down by {money(value)}.</> : null}
              </span>
            )}
          />
          <div style={{ fontSize: 12, color: MUTED }}>Received {dayjs(t.receivedOn).format('D MMM YYYY')}{t.poId ? ` on ${t.poId}` : ''}.</div>
        </div>
      )}
    </Modal>
  );
};

export default ReturnGoodsWindow;
